import axios from "axios";
import { ApiResponse } from "@/types";
import { logger } from "@/services/LoggerService";

export const getPropertyTags = async (propertyId: string): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.get(`/api/tags/properties/${propertyId}`);
        return response.data;
    } catch (error: any) {
        logger.error("Failed to fetch tags", error, {
            propertyId,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to fetch tags");
    }
};

export async function createTag({
    name,
    userId,
    propertyId,
}: {
    name: string;
    userId: string;
    propertyId?: string;
}): Promise<ApiResponse<any>> {
    const query = propertyId ? `?propertyId=${propertyId}` : "";
    const response = await axios.post(`/api/tags${query}`, {
        name,
        userId,
    });
    return response.data;
}

export const getTagVoteCounts = async (taggableId: string, tagId: string): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.get(`/api/tags/${taggableId}/${tagId}/vote-counts`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getUserVote = async (taggableId: string, tagId: string, userId: string): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.get(`/api/tags/${taggableId}/${tagId}/vote`, {
            params: { userId },
        });

        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const submitTagVote = async (
    taggableId: string,
    tagId: string,
    userId: string,
    voteType: string,
): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.post(`/api/tags/${taggableId}/${tagId}/vote`, {
            userId,
            voteType,
        });
        return response.data;
    } catch (error: any) {
        logger.error("Failed to submit tag vote", error, {
            taggableId,
            tagId,
            userId,
            voteType,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to submit vote");
    }
};

export const getTagComments = async (
    propertyId: string,
    tagId: string,
    page: number = 1,
    limit: number = 10,
): Promise<ApiResponse<any>> => {
    try {
        const query = new URLSearchParams({
            page: String(page),
            limit: String(limit),
        }).toString();
        const response = await axios.get(`/api/tags/properties/${propertyId}/comments/${tagId}?${query}`);
        return response.data;
    } catch (error: any) {
        logger.error("Failed to fetch tag comments", error, {
            propertyId,
            tagId,
            page,
            limit,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to fetch tag comments");
    }
};

export const createTagComment = async (
    userId: string,
    propertyId: string,
    tagId: string,
    content: string,
    parentId: string | null = null,
): Promise<ApiResponse<any>> => {
    try {
        const data = {
            userId,
            content,
            parentId,
        };
        const response = await axios.post(`/api/tags/properties/${propertyId}/comments/${tagId}`, data);
        return response.data;
    } catch (error: any) {
        logger.error("Failed to create tag comment", error, {
            userId,
            propertyId,
            tagId,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to create tag comment");
    }
};

export const deleteTagComment = async (
    userId: string,
    propertyId: string,
    tagId: string,
    commentId: string,
): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.delete(
            `/api/tags/properties/${propertyId}/comments/${tagId}/${commentId}?userId=${userId}`,
        );
        return response.data;
    } catch (error: any) {
        logger.error("Failed to delete tag comment", error, {
            userId,
            propertyId,
            tagId,
            commentId,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to delete tag comment");
    }
};

export const deleteTag = async (userId: string, propertyId: string, tagId: string): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.delete(`/api/tags/properties/${propertyId}/${tagId}?userId=${userId}`);
        return response.data;
    } catch (error: any) {
        logger.error("Failed to delete tag", error, {
            userId,
            propertyId,
            tagId,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to delete tag");
    }
};

export const getPropertyTagsComments = async (
    propertyId: string,
    page: number = 1,
    limit: number = 10,
): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.get(`/api/tags/properties/${propertyId}/comments`, {
            params: {
                page: Number(page),
                limit: Number(limit),
            },
        });
        return response.data;
    } catch (error: any) {
        logger.error("Failed to fetch property tags comments", error, {
            propertyId,
            page,
            limit,
            context: "TagsService",
        });
        throw new Error(error.response?.data?.message || "Failed to fetch property tags comments");
    }
};
